import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X, Send, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { ScrollArea } from "@/components/ui/scroll-area";

export default function AIChatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "bot",
      text: "Hola, soy el asistente virtual de Merza Soluciones (Z&M+Arquitectos). ¿En qué puedo ayudarte hoy?",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `chat-${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput("");
    setMessages((prev) => [...prev, { role: "user", text: userMsg }]);
    setLoading(true);

    try {
      const res = await api.sendChat({ message: userMsg, session_id: sessionId });
      setMessages((prev) => [...prev, { role: "bot", text: res.data.response }]);
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: "bot", text: "Disculpa, tuve un problema. Intenta de nuevo o llámanos al (55) 4442-1964." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Trigger button */}
      <AnimatePresence>
        {!open && (
          <motion.button
            data-testid="chatbot-trigger"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            onClick={() => setOpen(true)}
            className="fixed bottom-8 right-8 w-14 h-14 md:w-16 md:h-16 rounded-full bg-black text-white shadow-[0_20px_40px_rgba(0,0,0,0.2)] flex items-center justify-center hover:scale-110 transition-transform cursor-pointer z-50"
          >
            <MessageSquare size={22} strokeWidth={1.5} />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat window */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chatbot-window"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-4 right-4 md:bottom-8 md:right-8 w-[calc(100vw-2rem)] md:w-[380px] h-[500px] bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.15)] border border-neutral-100 flex flex-col overflow-hidden z-50"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-100">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-black flex items-center justify-center">
                  <MessageSquare size={14} className="text-white" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-sm font-medium text-neutral-900" style={{ fontFamily: "Outfit, sans-serif" }}>
                    Merza Soluciones
                  </p>
                  <p className="text-xs text-neutral-400">Z&M+Arquitectos</p>
                </div>
              </div>
              <button
                data-testid="chatbot-close"
                onClick={() => setOpen(false)}
                className="text-neutral-400 hover:text-neutral-900 transition-colors"
              >
                <X size={18} strokeWidth={1.5} />
              </button>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 px-5 py-4">
              <div className="space-y-4">
                {messages.map((msg, i) => (
                  <div
                    key={i}
                    className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                    data-testid={`chat-message-${i}`}
                  >
                    <div
                      className={`max-w-[80%] px-4 py-3 text-sm leading-relaxed ${
                        msg.role === "user"
                          ? "bg-black text-white rounded-2xl rounded-br-sm"
                          : "bg-neutral-100 text-neutral-800 rounded-2xl rounded-bl-sm"
                      }`}
                    >
                      {msg.text}
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="flex justify-start">
                    <div className="bg-neutral-100 rounded-2xl rounded-bl-sm px-4 py-3">
                      <Loader2 size={16} className="animate-spin text-neutral-400" />
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            {/* Input */}
            <div className="border-t border-neutral-100 px-4 py-3">
              <div className="flex items-center gap-2">
                <input
                  data-testid="chatbot-input"
                  type="text"
                  placeholder="Escribe un mensaje..."
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="flex-1 text-sm bg-neutral-50 rounded-full px-4 py-3 outline-none text-neutral-900 placeholder:text-neutral-400 border border-neutral-100 focus:border-neutral-300 transition-colors"
                />
                <button
                  data-testid="chatbot-send"
                  onClick={sendMessage}
                  disabled={!input.trim() || loading}
                  className="w-10 h-10 rounded-full bg-black text-white flex items-center justify-center hover:bg-neutral-800 transition-colors disabled:opacity-30"
                >
                  <Send size={14} strokeWidth={1.5} />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
